import React from 'react';

export function FlourTypes() {
  return (
    <div className="space-y-6 text-white" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Understanding Flour Types</h3>
        <p className="mb-4">
          Flour is the foundation of bread baking, and understanding the different types available will help you achieve better results. Each flour type has unique properties that affect hydration, gluten development, flavor, and texture.
        </p>
        
        <h4 className="text-lg text-amber-400 mt-6 mb-2">Wheat Flours</h4>
        
        <div className="space-y-4">
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">All-Purpose Flour</h5>
            <p className="text-sm text-white/80">
              A blend of hard and soft wheat with a protein content of 10-12%. Versatile for many baking applications but not ideal for high-hydration sourdough.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 10-12%</li>
              <li>Hydration capacity: Moderate</li>
              <li>Best for: Everyday baking, pancakes, cookies, basic breads</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Bread Flour</h5>
            <p className="text-sm text-white/80">
              Made from hard wheat with higher protein content (12-14%), creating stronger gluten networks ideal for sourdough.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 12-14%</li>
              <li>Hydration capacity: High</li>
              <li>Best for: Artisan breads, sourdough, bagels</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Whole Wheat Flour</h5>
            <p className="text-sm text-white/80">
              Contains the entire wheat kernel (bran, germ, and endosperm). Higher in nutrients but the bran particles can cut through gluten strands.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 13-14%</li>
              <li>Hydration capacity: Very high (absorbs more water)</li>
              <li>Best for: Hearty breads, nutritious loaves</li>
              <li>Note: Often mixed with bread flour for better structure</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">00 Flour (Tipo 00)</h5>
            <p className="text-sm text-white/80">
              Very finely ground Italian-style flour, often used for pizza and pasta. Protein content varies.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: Varies (9-12%)</li>
              <li>Hydration capacity: Moderate</li>
              <li>Best for: Pizza, focaccia, pasta</li>
            </ul>
          </div>
        </div>
        
        <h4 className="text-lg text-amber-400 mt-6 mb-2">Ancient & Heritage Wheat Varieties</h4>
        
        <div className="space-y-4">
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Einkorn</h5>
            <p className="text-sm text-white/80">
              One of the oldest wheat varieties with a simple genetic structure. Lower in gluten but rich in flavor.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 11-12%</li>
              <li>Hydration capacity: Lower</li>
              <li>Best for: Flatbreads, crackers, blending with stronger flours</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Spelt</h5>
            <p className="text-sm text-white/80">
              Ancient grain with a nutty flavor. Contains gluten but is often better tolerated by those with mild wheat sensitivities.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 12-13%</li>
              <li>Hydration capacity: High</li>
              <li>Best for: Artisan breads, pancakes, muffins</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Emmer (Farro)</h5>
            <p className="text-sm text-white/80">
              Ancient wheat with a rich, earthy flavor. Higher in protein than modern wheat.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 12-14%</li>
              <li>Hydration capacity: Moderate to high</li>
              <li>Best for: Rustic breads, blending with bread flour</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Khorasan (Kamut)</h5>
            <p className="text-sm text-white/80">
              Ancient grain with large kernels and a buttery, nutty flavor. High in protein.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 14-15%</li>
              <li>Hydration capacity: High</li>
              <li>Best for: Artisan breads, pasta</li>
            </ul>
          </div>
        </div>
        
        <h4 className="text-lg text-amber-400 mt-6 mb-2">Non-Wheat Flours</h4>
        
        <div className="space-y-4">
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Rye Flour</h5>
            <p className="text-sm text-white/80">
              Contains less gluten-forming proteins but more pentosans, which absorb water and create a sticky dough.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 9-12%</li>
              <li>Hydration capacity: Very high</li>
              <li>Best for: Rye breads, sourdough blends</li>
              <li>Note: Often mixed with wheat flour for better structure</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h5 className="font-medium">Barley Flour</h5>
            <p className="text-sm text-white/80">
              Low in gluten with a mild, sweet flavor. Often used in small quantities for flavor.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Protein: 8-11%</li>
              <li>Hydration capacity: Moderate</li>
              <li>Best for: Flatbreads, blending (5-10%) with wheat flours</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Effects on Bread</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Structure & Texture</h4>
            <p className="text-sm text-white/80">
              Higher protein flours (bread flour, high-gluten) create stronger gluten networks, resulting in better structure and more open crumb. Whole grain flours contain bran particles that can cut through gluten strands, resulting in denser loaves unless properly hydrated and fermented.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Flavor Profile</h4>
            <p className="text-sm text-white/80">
              Whole grain and ancient grain flours contribute more complex flavors than refined white flours. Rye flour adds distinctive tanginess that complements sourdough fermentation. The freshness of flour significantly impacts flavor—freshly milled flour offers more aromatic compounds.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Fermentation Rate</h4>
            <p className="text-sm text-white/80">
              Whole grain flours ferment faster due to higher enzyme activity and mineral content. White flours generally ferment more slowly and predictably. Rye flour ferments very quickly due to its unique carbohydrate composition.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Crust Development</h4>
            <p className="text-sm text-white/80">
              Higher protein flours typically develop darker, thicker crusts due to more Maillard reaction (browning). Flours with higher sugar content (like barley) brown more quickly. Whole grain flours often produce heartier, more rustic crusts.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Practical Tips</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Blending Flours</h4>
            <p className="text-sm text-white/80">
              For best results, especially when working with whole grain or ancient grain flours, consider blending with bread flour. A common approach is to use 70-80% bread flour with 20-30% specialty flour for balanced structure and flavor.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Storage</h4>
            <p className="text-sm text-white/80">
              Whole grain flours contain oils that can go rancid. Store them in airtight containers in a cool, dark place or refrigerate/freeze for longer storage. White flours are more stable but still benefit from proper storage.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Adjusting Recipes</h4>
            <p className="text-sm text-white/80">
              When substituting different flour types in recipes:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Whole wheat flour: Increase hydration by 5-10% and expect longer fermentation</li>
              <li>Rye flour: Increase hydration by 10-15% and reduce fermentation time</li>
              <li>Ancient grains: May require more water and gentler handling</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}